'use client';

import { Toast } from '@base-ui/react';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { X } from 'react-bootstrap-icons';

import { DataTestIds } from '@repo/frontend-shared';
import { SDSFlexbox } from '@repo/sds/components/SDSFlexbox';
import { SDSIconButton } from '@repo/sds/components/SDSIconButton';
import { SDSText } from '@repo/sds/components/SDSText';
import { ErrorIcon } from '@repo/sds/components/SDSToast/icons/ErrorIcon';
import { InfoIcon } from '@repo/sds/components/SDSToast/icons/InfoIcon';
import { SuccessIcon } from '@repo/sds/components/SDSToast/icons/SuccessIcon';
import { WarningIcon } from '@repo/sds/components/SDSToast/icons/WarningIcon';
import { useSDSToast } from '@repo/sds/components/SDSToast/use-sds-toast';
import { SDSToastType } from '@repo/sds/utils/toast';

import '@repo/sds/components/SDSToast/SDSToast.scss';

function isSDSToastType(value: unknown): value is SDSToastType {
  return (
    value === SDSToastType.Error ||
    value === SDSToastType.Success ||
    value === SDSToastType.Warning ||
    value === SDSToastType.Info
  );
}

function ToastIcon({ type }: { type: SDSToastType }) {
  switch (type) {
    case SDSToastType.Error:
      return <ErrorIcon />;
    case SDSToastType.Success:
      return <SuccessIcon />;
    case SDSToastType.Warning:
      return <WarningIcon />;
    case SDSToastType.Info:
      return <InfoIcon />;
    default:
      return <SuccessIcon />;
  }
}

interface SDSToastViewportProps {
  children?: ReactNode;
}
export function SDSToastViewport({ children }: SDSToastViewportProps) {
  const { toasts, close } = useSDSToast();

  return (
    <Toast.Portal>
      <Toast.Viewport className="SDSToast__viewport">
        {toasts.map(toast => {
          // Safely extract the type from toast data
          const toastData = toast.data;
          const type =
            toastData && typeof toastData === 'object' && 'type' in toastData && isSDSToastType(toastData.type)
              ? toastData.type
              : SDSToastType.Info;

          return (
            <Toast.Root
              key={toast.id}
              toast={toast}
              swipeDirection="right"
              className={clsx('SDSToast', `SDSToast--${type.toLowerCase()}`)}
              data-testid={DataTestIds.COMMON.TOAST}
            >
              <SDSFlexbox alignItems="center" gap="text-large">
                <ToastIcon type={type} />
                <Toast.Title>
                  <SDSText text={toast.title} type="body-large" color="on-brand" />
                </Toast.Title>
              </SDSFlexbox>
              <Toast.Close
                render={props => (
                  <SDSIconButton
                    {...props}
                    icon={<X />}
                    size="big"
                    color="inactive"
                    onClick={() => close(toast.id)}
                    ariaLabel="Close"
                  />
                )}
              />
            </Toast.Root>
          );
        })}
        {children}
      </Toast.Viewport>
    </Toast.Portal>
  );
}
